<template>
  <v-dialog
    v-model="visible"
    persistent
    fullscreen
    transition="dialog-bottom-transition"
  >
    <v-card class="wizard-card">
      <v-toolbar class="glass-toolbar" flat>
        <v-toolbar-title class="toolbar-title"
          >Postavljanje Nove Firme</v-toolbar-title
        >
        <v-spacer />
        <v-btn icon @click="handleClose" class="close-btn">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-stepper v-model="currentStep" :alt-labels="$vuetify.breakpoint.mdAndUp" class="glass-stepper">
        <v-stepper-header class="stepper-header">
          <v-stepper-step
            :complete="currentStep > 1"
            step="1"
            color="#3989C9"
            class="stepper-step"
          >
            Firma
          </v-stepper-step>
          <v-divider class="stepper-divider" />
          <v-stepper-step
            :complete="currentStep > 2"
            step="2"
            color="#3989C9"
            class="stepper-step"
          >
            Certifikat
          </v-stepper-step>
          <v-divider class="stepper-divider" />
          <v-stepper-step
            :complete="currentStep > 3"
            step="3"
            color="#3989C9"
            class="stepper-step"
          >
            Prodajno mjesto
          </v-stepper-step>
          <v-divider class="stepper-divider" />
          <v-stepper-step
            :complete="currentStep > 4"
            step="4"
            color="#3989C9"
            class="stepper-step"
          >
            Blagajna
          </v-stepper-step>
          <v-divider class="stepper-divider" />
          <v-stepper-step
            :complete="currentStep > 5"
            step="5"
            color="#3989C9"
            class="stepper-step"
          >
            Cjenik
          </v-stepper-step>
          <v-divider class="stepper-divider" />
          <v-stepper-step
            :complete="currentStep > 6"
            step="6"
            color="#3989C9"
            class="stepper-step"
          >
            Prodavači
          </v-stepper-step>
        </v-stepper-header>

        <div class="wizard-scroll-area">
          <v-container fluid>
            <v-row justify="center">
              <v-col cols="12" md="10" lg="8">
                <v-stepper-items>
                  <!-- Korak 1: Firma -->
                  <v-stepper-content step="1">
                    <v-card class="mb-4 pa-4" elevation="0">
                      <step-company
                        ref="stepCompany"
                        v-model="companyData"
                        @change="onCompanyChange"
                      />
                    </v-card>
                  </v-stepper-content>

                  <!-- Korak 2: Certifikat -->
                  <v-stepper-content step="2">
                    <v-card class="mb-4 pa-4" elevation="0">
                      <step-certificate
                        ref="stepCertificate"
                        v-model="certificateData"
                        @change="onCertificateChange"
                      />
                    </v-card>
                  </v-stepper-content>

                  <!-- Korak 3: Prodajno mjesto -->
                  <v-stepper-content step="3">
                    <v-card class="mb-4 pa-4" elevation="0">
                      <step-point-of-sale
                        ref="stepPointOfSale"
                        v-model="pointOfSaleData"
                        :existing-locations="existingLocations"
                        :company-data="companyData"
                        @change="onPointOfSaleChange"
                      />
                    </v-card>
                  </v-stepper-content>

                  <!-- Korak 4: Blagajna -->
                  <v-stepper-content step="4">
                    <v-card class="mb-4 pa-4" elevation="0">
                      <step-cash-register
                        ref="stepCashRegister"
                        v-model="cashRegisterData"
                        :company-data="companyData"
                        @change="onCashRegisterChange"
                      />
                    </v-card>
                  </v-stepper-content>

                  <!-- Korak 5: Cjenik -->
                  <v-stepper-content step="5">
                    <v-card class="mb-4 pa-4" elevation="0">
                      <step-price-list
                        ref="stepPriceList"
                        v-model="priceListData"
                        :company-data="companyData"
                        @change="onPriceListChange"
                      />
                    </v-card>
                  </v-stepper-content>

                  <!-- Korak 6: Prodavači -->
                  <v-stepper-content step="6">
                    <v-card class="mb-4 pa-4" elevation="0">
                      <step-sellers
                        ref="stepSellers"
                        :company-id="createdCompanyId"
                        :location-id="createdLocationId"
                        :domain="companyDomain"
                        @domainCreated="onDomainCreated"
                      />
                    </v-card>
                  </v-stepper-content>
                </v-stepper-items>
              </v-col>
            </v-row>
          </v-container>
        </div>
      </v-stepper>

      <!-- Akcijski gumbi - fiksirani footer -->
      <v-card-actions class="wizard-footer-actions">
        <v-btn
          v-if="currentStep > 1"
          text
          @click="prevStep"
          :disabled="submitting"
          class="back-btn"
        >
          <v-icon left>mdi-chevron-left</v-icon>
          Natrag
        </v-btn>
        <v-spacer />
        <v-btn
          v-if="currentStep === 2"
          text
          @click="skipCertificate"
          :disabled="submitting"
          class="skip-btn mr-2"
        >
          Preskoči
        </v-btn>
        <v-btn
          v-if="currentStep === 6"
          text
          @click="skipSellers"
          :disabled="submitting"
          class="skip-btn mr-2"
        >
          Preskoči
        </v-btn>
        <v-btn
          v-if="currentStep < 6"
          @click="nextStep"
          :disabled="!canGoNext"
          :loading="submitting"
          class="next-btn"
          elevation="0"
        >
          Dalje
          <v-icon right>mdi-chevron-right</v-icon>
        </v-btn>
        <v-btn
          v-else
          @click="finish"
          :disabled="!canGoNext"
          :loading="submitting"
          class="finish-btn"
          elevation="0"
        >
          <v-icon left>mdi-check</v-icon>
          Završi
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Confirm dialog -->
    <v-dialog v-model="confirmDialog" max-width="450" persistent>
      <v-card class="confirm-dialog-card">
        <v-card-title class="confirm-dialog-title"
          >Odustani od postavljanja?</v-card-title
        >
        <v-card-text class="confirm-dialog-text">
          Sigurni ste da želite odustati? Svi uneseni podaci će biti izgubljeni.
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn text @click="confirmDialog = false" class="cancel-dialog-btn"
            >Ne</v-btn
          >
          <v-btn @click="closeAndReset" class="confirm-dialog-btn" elevation="0"
            >Da, odustani</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Navigation warning dialog -->
    <v-dialog v-model="navigationWarningDialog" max-width="500" persistent>
      <v-card class="confirm-dialog-card">
        <v-card-title class="confirm-dialog-title">
          <v-icon left color="warning" size="28">mdi-alert</v-icon>
          Napustiti postavljanje?
        </v-card-title>
        <v-card-text class="confirm-dialog-text">
          Trenutno ste u procesu postavljanja nove firme. Ako napustite stranicu, svi uneseni podaci će biti izgubljeni i morat ćete početi ispočetka.
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn text @click="cancelNavigation" class="cancel-dialog-btn">
            Ostani ovdje
          </v-btn>
          <v-btn @click="confirmNavigation" class="confirm-dialog-btn" elevation="0">
            Da, napusti
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import StepCompany from './steps/StepCompany.vue'
import StepCertificate from './steps/StepCertificate.vue'
import StepPointOfSale from './steps/StepPointOfSale.vue'
import StepPriceList from './steps/StepPriceList.vue'
import StepCashRegister from './steps/StepCashRegister.vue'
import StepSellers from './steps/StepSellers.vue'
import { df, auth, functions } from '@/plugins/firebase'
import { v4 as uuidv4 } from 'uuid'
import state from '@/state'

export default {
  name: 'SetupWizard',
  components: {
    StepCompany,
    StepCertificate,
    StepPointOfSale,
    StepPriceList,
    StepCashRegister,
    StepSellers
  },
  inject: ['showMsgBox', 'confirm', 'confirmClose'],
  beforeRouteLeave (to, from, next) {
    // Ako je wizard zatvoren ili završen, dopusti navigaciju
    if (!this.visible || this.wizardCompleted) {
      next()
      return
    }

    // Ako ima unesenih podataka, prikaži upozorenje
    if (this.currentStep > 1 || Object.keys(this.companyData).length > 0) {
      this.pendingNavigation = next
      this.navigationWarningDialog = true
    } else {
      next()
    }
  },
  data () {
    return {
      visible: false,
      currentStep: 1,
      submitting: false,
      confirmDialog: false,
      navigationWarningDialog: false,
      wizardCompleted: false,
      pendingNavigation: null,
      companyData: {},
      certificateData: {},
      pointOfSaleData: {},
      priceListData: {},
      cashRegisterData: {},
      createdCompanyId: null,
      createdLocationId: null,
      createdCashRegisterId: null,
      createdCategoryId: null,
      createdItemId: null,
      companyDomain: '',
      existingLocations: []
    }
  },
  watch: {
    currentStep (step) {
      if (step === 4) {
        this.$nextTick(() => {
          this.logCashRegisterStep()
          if (this.$refs.stepCashRegister) this.$refs.stepCashRegister.focusNumber()
        })
      }
      if (step === 5) {
        this.$nextTick(() => {
          if (this.$refs.stepPriceList) this.$refs.stepPriceList.focusItemName()
        })
      }
    }
  },
  computed: {
    canGoNext () {
      switch (this.currentStep) {
        case 1:
          // Provjeri da li su osnovna polja popunjena
          return !!(
            this.companyData.name &&
            this.companyData.oib &&
            this.companyData.address &&
            this.companyData.city &&
            this.companyData.country &&
            this.companyData.zip_code
          )
        case 2:
          // Za certifikat korak, omoguci Dalje samo ako je certifikat i lozinka popunjeni
          // ili ako korisnik želi preskočiti (može kliknuti Preskoči)
          if (!this.certificateData.certificate) {
            return true // Može preskočiti
          }
          return !!(this.certificateData.certificate && this.certificateData.password)
        case 3:
          // Provjeri da li su osnovna polja za prodajno mjesto popunjena
          return !!(
            this.pointOfSaleData.name &&
            this.pointOfSaleData.location_id &&
            this.pointOfSaleData.address &&
            this.pointOfSaleData.city &&
            this.pointOfSaleData.zip_code
          )
        case 4:
          // Provjeri da li su osnovna polja za blagajnu popunjena
          return !!(
            this.cashRegisterData.name &&
            this.cashRegisterData.number
          )
        case 5: {
          const cats = this.priceListData.categories
          if (!cats || cats.length === 0) return false
          return cats.some(cat =>
            cat.name &&
            cat.items &&
            cat.items.some(item => item.name && item.price > 0)
          )
        }
        case 6:
          // Prodavači su opcionalni, uvijek može dalje
          return true
        default:
          return false
      }
    }
  },
  mounted () {
    // Dodaj listener za browser refresh/close
    window.addEventListener('beforeunload', this.handleBeforeUnload)
  },
  beforeDestroy () {
    window.removeEventListener('beforeunload', this.handleBeforeUnload)
  },
  methods: {
    handleBeforeUnload (event) {
      // Ako je wizard otvoren i nisu završili, upozori korisnika
      if (this.visible && !this.wizardCompleted && (this.currentStep > 1 || Object.keys(this.companyData).length > 0)) {
        event.preventDefault()
        event.returnValue = '' // Chrome requires returnValue to be set
        return ''
      }
    },
    cancelNavigation () {
      // Zatvori dialog i ostani na wizardu
      this.navigationWarningDialog = false
      if (this.pendingNavigation) {
        this.pendingNavigation(false)
        this.pendingNavigation = null
      }
    },
    confirmNavigation () {
      // Potvrdi napuštanje wizarda
      this.navigationWarningDialog = false
      this.wizardCompleted = true // Mark kao završen da ne triggerira ponovno
      this.visible = false

      // Reset wizarda
      setTimeout(() => {
        this.resetWizard()
      }, 300)

      if (this.pendingNavigation) {
        this.pendingNavigation()
        this.pendingNavigation = null
      }
    },
    resetWizard () {
      // Reset svih podataka na početne vrijednosti SA default vrijednostima
      this.currentStep = 1
      this.wizardCompleted = false
      this.submitting = false
      this.confirmDialog = false
      this.navigationWarningDialog = false
      this.pendingNavigation = null

      // Reset sa default vrijednostima
      this.companyData = {}
      this.certificateData = {}
      this.pointOfSaleData = {}
      this.priceListData = {}
      this.cashRegisterData = {}
      this.createdCompanyId = null
      this.createdLocationId = null
      this.createdCashRegisterId = null
      this.createdCategoryId = null
      this.createdItemId = null
      this.companyDomain = ''
      this.existingLocations = []

      // Reset child komponenti ako postoje
      this.$nextTick(() => {
        if (this.$refs.stepCompany) {
          // Force reset forme
          if (this.$refs.stepCompany.$refs.form) {
            this.$refs.stepCompany.$refs.form.reset()
            this.$refs.stepCompany.$refs.form.resetValidation()
          }
          // Vrati default vrijednosti za zemlju
          this.$refs.stepCompany.company.country = 'Hrvatska'
          this.$refs.stepCompany.$emit('input', this.$refs.stepCompany.company)
        }
        if (this.$refs.stepCertificate && this.$refs.stepCertificate.$refs.form) {
          this.$refs.stepCertificate.$refs.form.reset()
          this.$refs.stepCertificate.$refs.form.resetValidation()
        }
        if (this.$refs.stepPointOfSale && this.$refs.stepPointOfSale.$refs.form) {
          this.$refs.stepPointOfSale.$refs.form.reset()
          this.$refs.stepPointOfSale.$refs.form.resetValidation()
        }
        if (this.$refs.stepPriceList && this.$refs.stepPriceList.$refs.form) {
          this.$refs.stepPriceList.$refs.form.resetValidation()
        }
        if (this.$refs.stepCashRegister) {
          if (this.$refs.stepCashRegister.$refs.form) {
            this.$refs.stepCashRegister.$refs.form.resetValidation()
          }
          this.$refs.stepCashRegister.resetToDefaults()
        }
      })
    },
    open () {
      this.visible = true
      this.resetWizard()

      // companyData je već resetovan u resetWizard()
    },
    handleClose () {
      if (this.currentStep > 1 || Object.keys(this.companyData).length > 0) {
        this.confirmDialog = true
      } else {
        this.close()
        // Reset wizarda nakon zatvaranja ako nema podataka
        setTimeout(() => {
          this.resetWizard()
        }, 100)
      }
    },
    close () {
      this.wizardCompleted = true // Mark kao završen prije zatvaranja
      this.visible = false
      this.confirmDialog = false
      this.navigationWarningDialog = false
    },
    closeAndReset () {
      // Zatvori wizard i resetuj sve podatke
      this.close()
      setTimeout(() => {
        this.resetWizard()
      }, 100)
    },
    onDomainCreated (domain) {
      // Ažuriraj domenu u wizard state
      this.companyDomain = domain
    },
    nextStep () {
      if (this.currentStep === 1) {
        this.submitCompany()
      } else if (this.currentStep === 2) {
        this.submitCertificate()
      } else if (this.currentStep === 3) {
        this.submitPointOfSale()
      } else if (this.currentStep === 4) {
        this.submitCashRegister()
      } else if (this.currentStep === 5) {
        this.submitPriceList()
      }
    },
    skipCertificate () {
      // Preskoči certifikat korak
      this.certificateData = {}
      this.currentStep = 3

      // Reset i inicijalizuj podatke u StepPointOfSale
      this.$nextTick(() => {
        if (this.$refs.stepPointOfSale) {
          this.$refs.stepPointOfSale.reset()
          this.$refs.stepPointOfSale.initializeData()
        }
      })
    },
    skipSellers () {
      // Preskoči prodavače i završi wizard
      this.finish()
    },
    prevStep () {
      if (this.currentStep > 1) {
        this.currentStep--
      }
    },
    onCompanyChange () {
      // Omogući real-time validaciju
    },
    onCertificateChange () {
      // Omogući real-time validaciju
    },
    onPointOfSaleChange () {
      // Omogući real-time validaciju
    },
    onPriceListChange () {
      // Omogući real-time validaciju
    },
    onCashRegisterChange () {
      // Omogući real-time validaciju
    },
    async submitPriceList () {
      if (!this.$refs.stepPriceList.validate()) {
        this.showMsgBox({
          text: 'Molimo ispunite sva obavezna polja',
          color: 'error'
        })
        return
      }

      // Ako je već kreirano (korisnik se vratio nazad), samo idi dalje
      if (this.createdItemId) {
        this.currentStep = 6
        return
      }

      this.submitting = true

      try {
        const { categories } = this.$refs.stepPriceList.getData()
        const companyitemApi = functions.httpsCallable('companyitemapi')

        for (const cat of categories) {
          if (!cat.name || !cat.items || cat.items.length === 0) continue

          // 1. Kreiraj kategoriju direktno u Firestore
          const categoryId = uuidv4()
          const categoryEntry = { id: categoryId, name: cat.name, status: 'OK', picture: '' }

          await df.doc(`company_categories/${this.createdCompanyId}`).set(
            { categories: { [categoryId]: categoryEntry } },
            { merge: true }
          )

          for (const item of cat.items) {
            if (!item.name || item.price <= 0) continue

            // 2. Kreiraj artikl
            const newItemId = uuidv4()
            const itemResult = await companyitemApi({
              action: {
                operation: 'set',
                entity: 'item',
                params: {
                  id: newItemId,
                  name: item.name,
                  type: 'GOODS',
                  company_id: this.createdCompanyId,
                  storage_unit: item.unit,
                  selling_unit: item.unit,
                  warehouses: [this.createdLocationId],
                  ...(item.kpdcode ? { kpdcode: item.kpdcode } : {})
                }
              }
            })

            if (!itemResult.data || itemResult.data.code !== 200) {
              throw new Error(itemResult.data?.message || `Greška pri kreiranju artikla: ${item.name}`)
            }

            this.createdItemId = newItemId

            // 3. Kreiraj cjenik
            const taxLabels = { 0: 'PDV 0%', 500: 'PDV 5%', 1300: 'PDV 13%', 2500: 'PDV 25%' }
            const taxName = taxLabels[item.taxRate] || 'PDV 0%'
            const priceListResult = await companyitemApi({
              action: {
                operation: 'set',
                entity: 'price_list',
                params: {
                  item_id: newItemId,
                  company_id: this.createdCompanyId,
                  warehouses: [this.createdLocationId],
                  pricing_info: {
                    active: true,
                    categories: [{ id: categoryId, name: cat.name }],
                    prices: {
                      EUR: {
                        active: true,
                        price: Math.round(item.price * 100),
                        taxes: [{ name: taxName, rate: item.taxRate }]
                      }
                    },
                    manual_price_input: false
                  }
                }
              }
            })

            if (!priceListResult.data || priceListResult.data.code !== 200) {
              throw new Error(priceListResult.data?.message || `Greška pri kreiranju cjenika za: ${item.name}`)
            }
          }
        }

        this.currentStep = 6
      } catch (error) {
        console.error('Error creating price list:', error)
        this.showMsgBox({
          text: error.message || 'Dogodila se greška pri kreiranju cjenika',
          color: 'error'
        })
      } finally {
        this.submitting = false
      }
    },
    logCashRegisterStep () {
      console.log('[StepCashRegister] companyData:', JSON.parse(JSON.stringify(this.companyData)))
      console.log('[StepCashRegister] cashRegisterData:', JSON.parse(JSON.stringify(this.cashRegisterData)))
      if (this.$refs.stepCashRegister) {
        console.log('[StepCashRegister] cashRegister (internal):', JSON.parse(JSON.stringify(this.$refs.stepCashRegister.cashRegister)))
      }
    },
    async submitCompany () {
      // Provjeri validaciju forme
      const stepCompany = this.$refs.stepCompany
      if (!stepCompany || !stepCompany.validate()) {
        this.showMsgBox({
          text: 'Molimo ispunite sva obavezna polja pravilno',
          color: 'error'
        })
        return
      }

      this.submitting = true

      try {
        const company = this.$refs.stepCompany.getData()

        // Sačuvaj company podatke za kasnije korištenje
        this.companyData = {
          name: company.name,
          address: company.address,
          city: company.city,
          zip_code: company.zip_code,
          country: company.country || 'Hrvatska'
        }

        // Reset pointOfSaleData da bi se podaci kopirali iz companyData
        this.pointOfSaleData = {}

        // Ako je firma već kreirana (korisnik se vratio nazad), samo idi dalje
        if (this.createdCompanyId) {
          this.currentStep = 2
          this.submitting = false
          return
        }

        // Provjera duplog OIB-a
        const duplicateOib = await this.checkDuplicateOib(company.oib)
        if (duplicateOib) {
          throw new Error(`OIB ${company.oib} je već registriran`)
        }

        // Priprema payloada
        const payload = {
          action: {
            operation: 'set',
            entity: 'company',
            resource_id: '',
            params: {
              ...company,
              user_id: auth.currentUser.uid,
              status: 'OK',
              resource_status: 'ACTIVE',
              print_eur: false,
              print_template: 'racun02',
              receipt_type: '',
              certificate: '',
              domain: '',
              kelner: false,
              warehouse: ''
            }
          }
        }

        // Slanje API poziva
        const companyApi = functions.httpsCallable('companyapi')
        const result = await companyApi(payload)

        if (result.data && result.data.code === 200) {
          // API je uspješno kreirao firmu, sada čekaj da se pojavi u Firestore-u
          await this.waitForCompanyCreation(company.oib)

          // Idi na certifikat korak
          this.currentStep = 2
        } else {
          throw new Error(result.data?.message || 'Dogodila se greška pri kreiranju firme')
        }
      } catch (error) {
        console.error('Error creating company:', error)
        this.showMsgBox({
          text: error.message || 'Dogodila se greška pri kreiranju firme',
          color: 'error'
        })
      } finally {
        this.submitting = false
      }
    },
    async submitCertificate () {
      // Provjeri da li postoji certifikat
      const certificateData = this.$refs.stepCertificate.getData()

      if (!certificateData) {
        // Nema certifikata, preskoči
        this.currentStep = 3

        // Reset i inicijalizuj podatke u StepPointOfSale
        this.$nextTick(() => {
          if (this.$refs.stepPointOfSale) {
            this.$refs.stepPointOfSale.reset()
            this.$refs.stepPointOfSale.initializeData()
          }
        })
        return
      }

      this.submitting = true

      try {
        this.showMsgBox({
          text: 'Dodajem certifikat...',
          color: 'info'
        })

        await this.uploadCertificate(certificateData)

        this.showMsgBox({
          text: 'Certifikat je uspješno dodan',
          color: 'success'
        })

        // Označi u companyData da certifikat postoji
        this.companyData.certificate = 'uploaded'

        this.currentStep = 3

        // Reset i inicijalizuj podatke u StepPointOfSale
        this.$nextTick(() => {
          if (this.$refs.stepPointOfSale) {
            this.$refs.stepPointOfSale.reset()
            this.$refs.stepPointOfSale.initializeData()
          }
        })
      } catch (error) {
        console.error('Error uploading certificate:', error)
        this.showMsgBox({
          text: error.message || 'Dogodila se greška pri dodavanju certifikata',
          color: 'error'
        })
      } finally {
        this.submitting = false
      }
    },
    async submitPointOfSale () {
      if (!this.$refs.stepPointOfSale.validate()) {
        this.showMsgBox({
          text: 'Molimo ispunite sva obavezna polja',
          color: 'error'
        })
        return
      }

      // Ako je prodajno mjesto već kreirano (korisnik se vratio nazad), samo idi dalje
      if (this.createdLocationId) {
        this.currentStep = 4
        this.submitting = false
        return
      }

      this.submitting = true

      try {
        const pointOfSale = this.$refs.stepPointOfSale.getData()
        const reqId = uuidv4()

        const payload = {
          action: {
            operation: 'set',
            entity: 'location',
            params: {
              name: pointOfSale.name,
              location_id: pointOfSale.location_id,
              address: pointOfSale.address,
              city: pointOfSale.city,
              zip_code: pointOfSale.zip_code,
              company_id: this.createdCompanyId
            }
          }
        }

        // Slanje zahtjeva
        await df.doc(`request/${reqId}`).set({
          user_id: auth.currentUser.uid,
          device_id: 'web',
          created: new Date().getTime().toString(),
          type: 'location',
          payload: btoa(unescape(encodeURIComponent(JSON.stringify(payload))))
        })

        // Čekanje na obradu
        await this.waitForLocationCreation(pointOfSale.location_id)

        this.currentStep = 4

      } catch (error) {
        console.error('Error creating point of sale:', error)
        this.showMsgBox({
          text: error.message || 'Dogodila se greška pri kreiranju prodajnog mjesta',
          color: 'error'
        })
      } finally {
        this.submitting = false
      }
    },
    async submitCashRegister () {
      if (!this.$refs.stepCashRegister.validate()) {
        this.showMsgBox({
          text: 'Molimo ispunite sva obavezna polja',
          color: 'error'
        })
        return
      }

      // Ako je blagajna već kreirana (korisnik se vratio nazad), samo idi dalje
      if (this.createdCashRegisterId) {
        this.currentStep = 5
        return
      }

      this.submitting = true

      try {
        const cashRegister = this.$refs.stepCashRegister.getData()
        const reqId = uuidv4()

        const payload = {
          action: {
            operation: 'set',
            entity: 'cash_register',
            params: {
              name: cashRegister.name,
              number: parseInt(cashRegister.number),
              company_id: this.createdCompanyId,
              location_id: this.createdLocationId,
              type: cashRegister.type,
              view_type: 'GROUP_BY_CATEGORIES',
              login_type: cashRegister.login_type,
              after_receipt_behaviour: cashRegister.after_receipt_behaviour,
              printer: cashRegister.printer,
              deposit: 0
            }
          }
        }

        // Slanje zahtjeva
        await df.doc(`request/${reqId}`).set({
          user_id: auth.currentUser.uid,
          device_id: 'web',
          created: new Date().getTime().toString(),
          type: 'cashregister',
          payload: btoa(unescape(encodeURIComponent(JSON.stringify(payload))))
        })

        // Čekanje na obradu
        await this.waitForCashRegisterCreation(parseInt(cashRegister.number))

        // Idi na korak 5 (Cjenik)
        this.currentStep = 5
      } catch (error) {
        console.error('Error creating cash register:', error)
        this.showMsgBox({
          text: error.message || 'Dogodila se greška pri kreiranju blagajne',
          color: 'error'
        })
      } finally {
        this.submitting = false
      }
    },
    async finish () {
      this.submitting = true

      try {
        // Prodavači su već dodani putem checkbox-a, samo prikažimo success poruku
        const selectedSellers = this.$refs.stepSellers ? this.$refs.stepSellers.getSelectedUsers() : []

        if (selectedSellers.length > 0) {
          this.showMsgBox({
            text: `Uspješno ste kreirali firmu, prodajno mjesto, blagajnu i dodali ${selectedSellers.length} prodavača!`,
            color: 'info'
          })
        } else {
          this.showMsgBox({
            text: 'Uspješno ste kreirali firmu, prodajno mjesto i blagajnu!',
            color: 'info'
          })
        }

        // Označi wizard kao završen prije zatvaranja
        this.wizardCompleted = true

        // Zatvaranje wizarda
        this.close()

        // Resetuj wizard ODMAH nakon zatvaranja
        setTimeout(() => {
          this.resetWizard()
        }, 100)

        // Redirekcija samo ako nisu već na Companies stranici
        if (this.$route.path !== '/companies') {
          this.$router.push({ path: '/companies' }).catch(() => {})
        }
      } catch (error) {
        console.error('Error finishing wizard:', error)
        this.showMsgBox({
          text: error.message || 'Dogodila se greška pri završavanju',
          color: 'error'
        })
      } finally {
        this.submitting = false
      }
    },
    async checkDuplicateOib (oib) {
      try {
        const snapshot = await df
          .collection('companies')
          .where('oib', '==', oib)
          .get()
        return !snapshot.empty
      } catch (error) {
        console.error('Error checking duplicate OIB:', error)
        return false
      }
    },
    async ensureCompanyDomain () {
      // Ako domena već postoji, ne radi ništa
      if (this.companyDomain) {
        return
      }

      // Učitaj trenutnu firmu iz Firestore-a
      const companyDoc = await df.doc(`companies/${this.createdCompanyId}`).get()
      if (!companyDoc.exists) {
        console.error('Company not found')
        return
      }

      const company = companyDoc.data()

      // Pitaj korisnika da li želi kreirati domenu
      const confirmed = await this.confirm({
        title: 'Kreiranje domene',
        message: 'Firma nema domenu. Domena je potrebna za kreiranje korisnika sa email adresom u formatu "username@domena.aikasa". Da li želite kreirati domenu?',
        options: {
          toolbar: false,
          width: 500,
          confirmText: 'Da, kreiraj',
          cancelText: 'Ne, preskoči'
        }
      })

      if (confirmed) {
        try {
          this.showMsgBox({
            text: 'Kreiram domenu...',
            color: 'info'
          })

          const domain = await this.createCompanyDomain(company)
          this.companyDomain = domain

          this.showMsgBox({
            text: `Domena "${domain}" je uspješno kreirana`,
            color: 'success'
          })
        } catch (error) {
          console.error('Error creating domain:', error)
          this.showMsgBox({
            text: 'Greška pri kreiranju domene. Možete nastaviti bez domene.',
            color: 'warning'
          })
        }
      }
    },
    async createCompanyDomain (company) {
      try {
        // Generiraj domenu od imena firme (samo slova i brojevi, lowercase)
        const baseDomain = company.name
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '') // Ukloni dijakritičke znakove
          .replace(/[^a-z0-9]/g, '') // Samo slova i brojevi
          .substring(0, 20) // Max 20 karaktera

        // Provjeri da li domena već postoji
        let domain = baseDomain
        let counter = 1
        let domainExists = true

        while (domainExists) {
          const snapshot = await df
            .collection('companies')
            .where('domain', '==', domain)
            .get()

          if (snapshot.empty) {
            domainExists = false
          } else {
            domain = `${baseDomain}${counter}`
            counter++
          }
        }

        // Ažuriraj firmu sa domenom
        const companyApi = functions.httpsCallable('companyapi')
        await companyApi({
          action: {
            operation: 'update',
            entity: 'company',
            resource_id: company.id,
            params: {
              domain
            }
          }
        })

        return domain
      } catch (error) {
        console.error('Error creating company domain:', error)
        throw error
      }
    },
    waitForCompanyCreation (oib) {
      return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          unsubscribe()
          reject(new Error('Timeout - firma nije kreirana'))
        }, 30000) // 30 sekundi timeout

        const docRef = df.doc(`user_companies/${auth.currentUser.uid}`)

        const unsubscribe = docRef.onSnapshot((doc) => {
          if (doc.exists && doc.data() && doc.data().companies) {
            const companies = doc.data().companies
            const company = Object.values(companies).find(
              comp => comp.oib === oib && comp.resource_status === 'ACTIVE'
            )

            if (company) {
              clearTimeout(timeout)
              unsubscribe()
              this.createdCompanyId = company.id
              this.companyDomain = company.domain || ''

              // Postavljanje trenutne firme u state
              state.setCurrentCompany(company)
              state.setAppTitle(company.name)

              resolve()
            }
          }
        }, (error) => {
          clearTimeout(timeout)
          unsubscribe()
          reject(error)
        })
      })
    },
    waitForLocationCreation (locationId) {
      return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          unsubscribe()
          reject(new Error('Timeout - prodajno mjesto nije kreirano'))
        }, 30000)

        const docRef = df.doc(
          `user_locations/${auth.currentUser.uid}.${this.createdCompanyId}`
        )

        const unsubscribe = docRef.onSnapshot((doc) => {
          if (doc.exists && doc.data() && doc.data().locations) {
            const locations = doc.data().locations
            const location = Object.values(locations).find(
              loc => loc.location_id === locationId
            )

            if (location && location.resource_status === 'ACTIVE') {
              clearTimeout(timeout)
              unsubscribe()
              this.createdLocationId = location.id
              state.setPointOfSale({
                id: location.id,
                ...location
              })
              resolve()
            }
          }
        }, (error) => {
          clearTimeout(timeout)
          unsubscribe()
          reject(error)
        })
      })
    },
    waitForCashRegisterCreation (registerNumber) {
      return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          unsubscribe()
          reject(new Error('Timeout - blagajna nije kreirana'))
        }, 60000)

        const docRef = df.doc(
          `user_cash_registers/${auth.currentUser.uid}.${this.createdCompanyId}.${this.createdLocationId}`
        )

        const unsubscribe = docRef.onSnapshot((doc) => {
          if (doc.exists && doc.data() && doc.data().cash_registers) {
            const registers = doc.data().cash_registers
            const register = Object.values(registers).find(
              reg => reg.number === registerNumber
            )

            if (register && (register.resource_status === 'ACTIVE' || register.status === 'OK')) {
              clearTimeout(timeout)
              unsubscribe()
              this.createdCashRegisterId = register.id
              resolve()
            }
          }
        }, (error) => {
          clearTimeout(timeout)
          unsubscribe()
          reject(error)
        })
      })
    },
    async uploadCertificate (certificateData) {
      return new Promise((resolve, reject) => {
        const reqId = uuidv4()

        const payload = {
          action: {
            operation: 'set',
            entity: 'certificate',
            params: {
              cert: certificateData.certificate,
              company_id: this.createdCompanyId,
              password: certificateData.password
            }
          }
        }

        // Slanje zahtjeva
        df.doc(`request/${reqId}`).set({
          msg_id: reqId,
          user_id: auth.currentUser.uid,
          device_id: 'web',
          created: new Date().getTime(),
          type: 'certificate',
          payload: btoa(unescape(encodeURIComponent(JSON.stringify(payload))))
        })

        // Postavi timeout
        const timeout = setTimeout(() => {
          unsubscribeResponse()
          unsubscribeError()
          reject(new Error('Timeout - certifikat nije upload-an'))
        }, 30000)

        // Listener za response
        const responseDocRef = df.doc(`response/${reqId}`)
        const unsubscribeResponse = responseDocRef.onSnapshot((doc) => {
          if (doc.exists && doc.data()) {
            const data = doc.data()
            if (data.header && data.header.code === 200) {
              clearTimeout(timeout)
              unsubscribeResponse()
              unsubscribeError()
              resolve()
            }
          }
        })

        // Listener za errors
        const errorCollectionRef = df.collection('errors')
        const unsubscribeError = errorCollectionRef
          .where('error_id', '==', `${auth.currentUser.uid}.${reqId}`)
          .onSnapshot((snapshot) => {
            if (!snapshot.empty) {
              clearTimeout(timeout)
              unsubscribeResponse()
              unsubscribeError()

              const errorDoc = snapshot.docs[0].data()
              let errorMessage = 'Greška pri upload-u certifikata'

              if (errorDoc.error_status && errorDoc.error_status.includes('Invalid certificate password:')) {
                errorMessage = 'Neispravna lozinka certifikata'
              } else if (errorDoc.error_status) {
                errorMessage = errorDoc.error_status
              }

              reject(new Error(errorMessage))
            }
          })
      })
    }
  }
}
</script>

<style scoped>
/* Wizard Card - flex column taking full dialog height */
.wizard-card {
  display: flex !important;
  flex-direction: column !important;
  height: 100% !important;
  overflow: hidden !important;
  background: rgba(255, 255, 255, 0.98) !important;
  backdrop-filter: blur(20px) !important;
}

/* Glass Toolbar - fixed at top, does not scroll */
.glass-toolbar {
  flex: 0 0 auto !important;
  z-index: 20 !important;
  background: linear-gradient(
    135deg,
    rgba(0, 179, 190, 0.95) 0%,
    rgba(57, 137, 201, 0.9) 50%,
    rgba(97, 84, 164, 0.95) 100%
  ) !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2) !important;
}

/* Glass Stepper - takes all remaining height, columns internally */
.glass-stepper {
  flex: 1 !important;
  display: flex !important;
  flex-direction: column !important;
  overflow: hidden !important;
  background: transparent !important;
  box-shadow: none !important;
  min-height: 0 !important;
}

/* Wizard Footer Actions - fixed at bottom, does not scroll */
.wizard-footer-actions {
  flex: 0 0 auto !important;
  z-index: 10 !important;
  padding: 12px 16px !important;
  border-top: 1px solid rgba(57, 137, 201, 0.1) !important;
  background: rgba(255, 255, 255, 0.98) !important;
}

/* Scrollable content area */
.wizard-scroll-area {
  flex: 1 !important;
  min-height: 0 !important;
  overflow-y: auto !important;
  -webkit-overflow-scrolling: touch !important;
  padding: 16px 32px 32px !important;
}

.toolbar-title {
  color: white !important;
  font-weight: 600 !important;
  font-size: 20px !important;
  letter-spacing: 0.3px !important;
}

.close-btn {
  color: white !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.2) !important;
  transform: scale(1.1);
}

.stepper-header {
  flex-shrink: 0 !important;
  background: rgba(255, 255, 255, 0.8) !important;
  backdrop-filter: blur(10px) !important;
  border-radius: 0 !important;
  padding: 0 16px !important;
  box-shadow: 0 2px 8px rgba(57, 137, 201, 0.1) !important;
  z-index: 15 !important;
}

.stepper-header::v-deep .v-stepper__step {
  padding: 8px 12px !important;
}

.stepper-divider {
  border-color: rgba(57, 137, 201, 0.2) !important;
}

.stepper-step::v-deep .v-stepper__step__step {
  background: rgba(255, 255, 255, 0.9) !important;
  border: 2px solid rgba(57, 137, 201, 0.3) !important;
  color: #3989c9 !important;
  font-weight: 600 !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.stepper-step.v-stepper__step--active::v-deep .v-stepper__step__step {
  background: linear-gradient(
    135deg,
    rgba(0, 179, 190, 0.9) 0%,
    rgba(57, 137, 201, 0.85) 50%,
    rgba(97, 84, 164, 0.9) 100%
  ) !important;
  border-color: rgba(255, 255, 255, 0.5) !important;
  color: white !important;
  box-shadow: 0 4px 12px rgba(57, 137, 201, 0.3) !important;
}

.stepper-step.v-stepper__step--complete::v-deep .v-stepper__step__step {
  background: rgba(76, 175, 80, 0.9) !important;
  border-color: rgba(76, 175, 80, 0.5) !important;
  color: white !important;
}

.stepper-step::v-deep .v-stepper__label {
  color: rgba(57, 137, 201, 0.8) !important;
  font-weight: 600 !important;
  font-size: 13px !important;
}

.stepper-step.v-stepper__step--active::v-deep .v-stepper__label {
  color: #3989c9 !important;
}

/* Back Button */
.back-btn {
  color: rgba(57, 137, 201, 0.8) !important;
  text-transform: none !important;
  font-weight: 600 !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.back-btn:hover {
  color: #3989c9 !important;
  background: rgba(57, 137, 201, 0.08) !important;
}

/* Skip Button */
.skip-btn {
  color: rgba(120, 120, 130, 0.8) !important;
  text-transform: none !important;
  font-weight: 600 !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.skip-btn:hover {
  color: rgba(120, 120, 130, 1) !important;
  background: rgba(120, 120, 130, 0.08) !important;
}

/* Next Button - Soft Shadow Style */
.next-btn {
  background: rgba(255, 255, 255, 0.8) !important;
  color: #3989c9 !important;
  border-radius: 12px !important;
  border: 1px solid rgba(57, 137, 201, 0.15) !important;
  box-shadow: 0 2px 8px rgba(57, 137, 201, 0.15) !important;
  backdrop-filter: blur(10px) !important;
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  letter-spacing: 0.3px !important;
  padding: 10px 24px !important;
  height: 40px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.next-btn:hover {
  box-shadow: 0 4px 12px rgba(57, 137, 201, 0.25) !important;
  border-color: rgba(57, 137, 201, 0.3) !important;
  transform: translateY(-1px);
}

.next-btn .v-icon {
  color: #3989c9 !important;
}

/* Finish Button - Success Green */
.finish-btn {
  background: rgba(76, 175, 80, 0.9) !important;
  color: white !important;
  border-radius: 12px !important;
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.25) !important;
  backdrop-filter: blur(10px) !important;
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  letter-spacing: 0.3px !important;
  padding: 10px 24px !important;
  height: 40px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.finish-btn:hover {
  background: rgba(76, 175, 80, 1) !important;
  transform: translateY(-2px) scale(1.02);
  box-shadow: 0 8px 20px rgba(76, 175, 80, 0.4) !important;
  border-color: rgba(255, 255, 255, 0.5) !important;
}

.finish-btn .v-icon {
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
}

/* Confirm Dialog */
.confirm-dialog-card {
  background: rgba(255, 255, 255, 0.98) !important;
  backdrop-filter: blur(20px) !important;
  border-radius: 16px !important;
  border: 1px solid rgba(57, 137, 201, 0.2) !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15) !important;
}

.confirm-dialog-title {
  color: #3989c9 !important;
  font-weight: 600 !important;
  font-size: 18px !important;
  letter-spacing: 0.3px !important;
  padding: 24px 24px 16px !important;
}

.confirm-dialog-text {
  color: rgba(0, 0, 0, 0.7) !important;
  font-size: 14px !important;
  line-height: 1.6 !important;
  padding: 0 24px 16px !important;
}

/* Cancel Dialog Button */
.cancel-dialog-btn {
  color: rgba(57, 137, 201, 0.8) !important;
  text-transform: none !important;
  font-weight: 600 !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.cancel-dialog-btn:hover {
  color: #3989c9 !important;
  background: rgba(57, 137, 201, 0.08) !important;
}

/* Confirm Dialog Button - Danger Style */
.confirm-dialog-btn {
  background: rgba(244, 67, 54, 0.9) !important;
  color: white !important;
  border-radius: 12px !important;
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
  box-shadow: 0 4px 12px rgba(244, 67, 54, 0.25) !important;
  backdrop-filter: blur(10px) !important;
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 13px !important;
  letter-spacing: 0.3px !important;
  padding: 8px 18px !important;
  height: 36px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.confirm-dialog-btn:hover {
  background: rgba(244, 67, 54, 1) !important;
  transform: translateY(-2px) scale(1.02);
  box-shadow: 0 8px 20px rgba(244, 67, 54, 0.4) !important;
  border-color: rgba(255, 255, 255, 0.5) !important;
}

/* Step Content Cards */
::v-deep .v-stepper__content .v-card {
  background: rgba(255, 255, 255, 0.6) !important;
  backdrop-filter: blur(10px) !important;
  border-radius: 12px !important;
  border: 1px solid rgba(57, 137, 201, 0.1) !important;
  box-shadow: none !important;
}

/* Mobile Optimizations */
@media (max-width: 960px) {
  /* Reduce content padding on tablets/mobile */
  .wizard-scroll-area {
    padding: 12px 12px 16px !important;
  }

  /* Compact stepper header for mobile - horizontal layout */
  .stepper-header {
    padding: 0 8px !important;
  }

  .stepper-header::v-deep .v-stepper__step {
    padding: 6px 8px !important;
  }

  /* Smaller step circles */
  .stepper-step::v-deep .v-stepper__step__step {
    width: 32px !important;
    height: 32px !important;
    min-width: 32px !important;
    font-size: 13px !important;
    border-width: 1.5px !important;
  }

  /* Compact step wrapper for horizontal layout */
  .stepper-step {
    flex-basis: auto !important;
    padding: 0 !important;
  }

  /* Thinner dividers */
  .stepper-divider {
    margin: 0 6px !important;
  }

  /* Reduce toolbar padding */
  .glass-toolbar {
    padding: 14px 16px !important;
  }

  .toolbar-title {
    font-size: 18px !important;
  }

  /* Compact card padding */
  ::v-deep .v-stepper__content .v-card {
    border-radius: 8px !important;
    padding: 16px !important;
  }

  /* Reduce gutters for better field width on tablets */
  ::v-deep .v-stepper__content .v-card .v-row {
    margin-left: -10px !important;
    margin-right: -10px !important;
  }

  ::v-deep .v-stepper__content .v-card .v-col {
    padding-left: 10px !important;
    padding-right: 10px !important;
  }

  /* Smaller buttons on mobile */
  .next-btn,
  .finish-btn,
  .skip-btn {
    font-size: 13px !important;
    padding: 8px 16px !important;
    height: 36px !important;
  }

  .back-btn {
    font-size: 13px !important;
  }
}

@media (max-width: 600px) {
  /* Minimal padding on phones for maximum content space */
  .wizard-scroll-area {
    padding: 8px 8px 12px !important;
  }

  /* Very compact stepper header on phones */
  .stepper-header {
    padding: 0 4px !important;
  }

  .stepper-header::v-deep .v-stepper__step {
    padding: 4px 4px !important;
  }

  /* Hide step labels on phones - show only step numbers */
  .stepper-step::v-deep .v-stepper__label {
    display: none !important;
  }

  .stepper-step::v-deep .v-stepper__step__step {
    width: 28px !important;
    height: 28px !important;
    min-width: 28px !important;
    font-size: 12px !important;
  }

  .stepper-divider {
    margin: 0 2px !important;
  }

  .toolbar-title {
    font-size: 16px !important;
  }

  /* Make content cards fill width with minimal padding */
  ::v-deep .v-stepper__content .v-card {
    padding: 12px !important;
    margin: 0 !important;
  }

  /* Reduce row gutters on mobile for more field space */
  ::v-deep .v-stepper__content .v-card .v-row {
    margin-left: -8px !important;
    margin-right: -8px !important;
  }

  ::v-deep .v-stepper__content .v-card .v-col {
    padding-left: 8px !important;
    padding-right: 8px !important;
  }
}

/* Extra small screens */
@media (max-width: 400px) {
  .wizard-scroll-area {
    padding: 4px 4px 8px !important;
  }

  .stepper-header {
    padding: 8px 4px !important;
  }

  .stepper-step::v-deep .v-stepper__step__step {
    width: 28px !important;
    height: 28px !important;
    min-width: 28px !important;
    font-size: 12px !important;
  }

  .toolbar-title {
    font-size: 16px !important;
  }

  ::v-deep .v-stepper__content .v-card {
    padding: 8px !important;
  }

  /* Ultra minimal gutters for tiny screens */
  ::v-deep .v-stepper__content .v-card .v-row {
    margin-left: -4px !important;
    margin-right: -4px !important;
  }

  ::v-deep .v-stepper__content .v-card .v-col {
    padding-left: 4px !important;
    padding-right: 4px !important;
  }
}
</style>
